import {Device} from '@fantasmagames/core/src/device/Device';
import {ResolutionManager} from '@fantasmagames/core/src/screen/ResolutionManager';
import {injectable} from 'inversify';
import {GameConfig, RecordData} from '@fantasmagames/slot/src/config/GameConfig';

export interface ILocaleFontMap {
	[id: string]: string;
}

@injectable()
export class TextStyles {
	protected locale: string;

	protected _config: GameConfig;

	protected _baseFontArial: PIXI.TextStyle;
	protected _cheatFontArial: PIXI.TextStyle;
	protected _titlesFont: PIXI.TextStyle;
	protected _specialTitlesFont: PIXI.TextStyle;
	protected _subtitlesFont: PIXI.TextStyle;
	protected _breadTextFont: PIXI.TextStyle;
	protected _breadTextHeavyFont: PIXI.TextStyle;
	protected _breadTextHeavyWhiteFont: PIXI.TextStyle;
	protected _guiInfoNumbers: PIXI.TextStyle;
	protected _guiInfoText: PIXI.TextStyle;
	protected _guiInfoWinFieldNumbers: PIXI.TextStyle;
	private _gameSpecificWinFieldNumbers: PIXI.TextStyle;
	protected _guiInfoWinFieldText: PIXI.TextStyle;
	protected _mainMenuTitle: PIXI.TextStyle;
	protected _mainMenuBread: PIXI.TextStyle;
	protected _settingsSubtitle: PIXI.TextStyle;
	protected _settingsDescription: PIXI.TextStyle;
	protected _autoplaySettingsDescription: PIXI.TextStyle;
	protected _buttonMenuTitleStyle: PIXI.TextStyle;
	protected _buttonBaseStyle: PIXI.TextStyle;
	protected _smallButtonBaseStyle: PIXI.TextStyle;
	protected _buttonOverStyle: PIXI.TextStyle;
	protected _buttonDownStyle: PIXI.TextStyle;
	protected _buttonDisabledStyle: PIXI.TextStyle;
	protected _limitErrorTextStyle: PIXI.TextStyle;
	protected _infoPopup: PIXI.TextStyle;
	protected _spinsLeftStyle: PIXI.TextStyle;
	protected _featureTextStyle: PIXI.TextStyle;
	protected _featureScreenTextStyle: PIXI.TextStyle;
	protected _featureScreenCaptionTextStyle: PIXI.TextStyle;
	protected _freeRoundsDisclaimerStyle: PIXI.TextStyle;
	protected _freeRoundsCaptionStyle: PIXI.TextStyle;
	protected _freeRoundsTextStyle: PIXI.TextStyle;
	protected _freeRoundsSmallTextStyle: PIXI.TextStyle;
	protected _freeRoundsButtonTextStyle: PIXI.TextStyle;
	protected _freeRoundsSuperSmallTextStyle: PIXI.TextStyle;
	protected _historyBoxFont: PIXI.TextStyle;
	protected _historyBoxMultiplierFont: PIXI.TextStyle;

	protected _resolutionManager: ResolutionManager;

	protected _specialFontMap: ILocaleFontMap = {
		el: 'Alegreya Sans Sc',
		bg: 'Alegreya Sans Sc',
		ru: 'Alegreya Sans Sc',
		th: 'Pridi Regular',
		ja: 'Nicokaku',
		ka: 'Calibri',
		standard: 'Black Han Sans',
	};

	protected _baseFontMap: ILocaleFontMap = {
		standard: 'Arial',
	};

	protected _strongFontMap: ILocaleFontMap = {
		th: 'Pridi Regular',
		standard: 'Arial Black',
		iOs: 'Verdana-Bold',
		iphone: 'Verdana-Bold',
	};

	protected _device: Device;

	private _gameRulesTextStyle: PIXI.TextStyle;

	constructor() {
	}

	public init(locale: string, resolutionManager: ResolutionManager, device: Device, config: any): void {
		this.locale = locale;
		this._resolutionManager = resolutionManager;
		this._device = device;
		this._config = new GameConfig();
		this._config.loadConfig(config);
		this.generateFonts(locale);
	}

	public generateFonts(locale: string): void {
		// const baseFontSize: number = this.device.isMobile ? 26 : 30;
		const smallFontSize: number = 18;
		const notTooSmallFontSize: number = 22;
		const baseFontSize: number = 26;
		const lowerMiddleFontSize: number = 30;
		const middleFontSize: number = 36;
		const bigFontSize: number = 42;
		const biggestFontSize: number = 46;

		const blackColor: string = '#000000';
		const standardColor: string = '#ffffff';
		const redColor: string = '#ff0000';
		const errorColor: string = '#ffffff';
		const titleColor: string = '#fadf47';
		const ocurrenceColor: string = '#efc107';

		const baseFontFamily: string = this.getFontFamily(locale, this._baseFontMap);
		let strongFontFamily: string = this.getFontFamily(locale, this._strongFontMap);

		if (this._device.iOS || this._device.iPhone) {
			strongFontFamily = this.getFontFamily('iOs', this._strongFontMap);
		}
		const specialFont: string = this.getFontFamily(locale, this._specialFontMap);

		const wordWrapWidth: number = this._resolutionManager.resolution.width * 0.8;

		this._freeRoundsCaptionStyle = new PIXI.TextStyle({
			fontFamily: this.getConfigData('freeRoundsCaptionStyle', 'fontFamily', baseFontFamily),
			fontSize: this.getConfigData('freeRoundsCaptionStyle', 'fontSize', bigFontSize),
			align: this.getConfigData('freeRoundsCaptionStyle', 'align', 'center'),
			fontWeight: this.getConfigData('freeRoundsCaptionStyle', 'fontWeight', 'bold'),
			wordWrap: true,
			wordWrapWidth,
			fill: this.getConfigData('freeRoundsCaptionStyle', 'fill', [standardColor]),
		});

		this._freeRoundsTextStyle = new PIXI.TextStyle({
			fontFamily: this.getConfigData('freeRoundsTextStyle', 'fontFamily', baseFontFamily),
			fontSize: this.getConfigData('freeRoundsTextStyle', 'fontSize', lowerMiddleFontSize),
			align: this.getConfigData('freeRoundsTextStyle', 'align', 'center'),
			fontWeight: this.getConfigData('freeRoundsTextStyle', 'fontWeight', 'bold'),
			wordWrap: true,
			wordWrapWidth,
			fill: this.getConfigData('freeRoundsTextStyle', 'fill', [standardColor]),
		});

		this._freeRoundsDisclaimerStyle = new PIXI.TextStyle({
			fontFamily: this.getConfigData('freeRoundsDisclaimerStyle', 'fontFamily', baseFontFamily),
			fontSize: this.getConfigData('freeRoundsDisclaimerStyle', 'fontSize', lowerMiddleFontSize),
			align: this.getConfigData('freeRoundsDisclaimerStyle', 'align', 'center'),
			fontWeight: this.getConfigData('freeRoundsDisclaimerStyle', 'fontWeight', 'bold'),
			wordWrap: true,
			wordWrapWidth,
			fill: this.getConfigData('freeRoundsDisclaimerStyle', 'fill', [redColor]),
		});

		this._freeRoundsSmallTextStyle = new PIXI.TextStyle({
			fontFamily: this.getConfigData('freeRoundsSmallTextStyle', 'fontFamily', baseFontFamily),
			fontSize: this.getConfigData('freeRoundsSmallTextStyle', 'fontSize', baseFontSize),
			align: this.getConfigData('freeRoundsSmallTextStyle', 'align', 'center'),
			fontWeight: this.getConfigData('freeRoundsSmallTextStyle', 'fontWeight', 'bold'),
			wordWrap: true,
			wordWrapWidth,
			fill: this.getConfigData('freeRoundsSmallTextStyle', 'fill', [standardColor]),
		});

		this._freeRoundsButtonTextStyle = new PIXI.TextStyle({
			fontFamily: this.getConfigData('freeRoundsButtonTextStyle', 'fontFamily', baseFontFamily),
			fontSize: this.getConfigData('freeRoundsButtonTextStyle', 'fontSize', baseFontSize),
			align: this.getConfigData('freeRoundsButtonTextStyle', 'align', 'center'),
			fontWeight: this.getConfigData('freeRoundsButtonTextStyle', 'fontWeight', 'bold'),
			fill: this.getConfigData('freeRoundsButtonTextStyle', 'fill', titleColor),
			stroke: this.getConfigData('freeRoundsButtonTextStyle', 'stroke', '#bbbb00'),
			wordWrap: true,
			wordWrapWidth,
		});

		this._freeRoundsSuperSmallTextStyle = new PIXI.TextStyle({
			fontFamily: this.getConfigData('freeRoundsSuperSmallTextStyle', 'fontFamily', baseFontFamily),
			fontSize: this.getConfigData('freeRoundsSuperSmallTextStyle', 'fontSize', smallFontSize),
			align: this.getConfigData('freeRoundsSuperSmallTextStyle', 'align', 'center'),
			fontWeight: this.getConfigData('freeRoundsSuperSmallTextStyle', 'fontWeight', 'bold'),
			wordWrap: true,
			wordWrapWidth,
			fill: this.getConfigData('freeRoundsSuperSmallTextStyle', 'fill', [standardColor]),
		});

		if (locale === 'ka') {
			this._freeRoundsCaptionStyle.padding = bigFontSize * 0.5;
			this._freeRoundsTextStyle.padding = lowerMiddleFontSize * 0.5;
			this._freeRoundsDisclaimerStyle.padding = lowerMiddleFontSize * 0.5;
			this._freeRoundsSmallTextStyle.padding = baseFontSize * 0.5;
			this._freeRoundsButtonTextStyle.padding = baseFontSize * 0.5;
			this._freeRoundsSuperSmallTextStyle.padding = smallFontSize * 0.5;
		}

		this._baseFontArial = new PIXI.TextStyle({
			fontFamily: this.getConfigData('baseFontArial', 'fontFamily', baseFontFamily),
			fontSize: this.getConfigData('baseFontArial', 'fontSize', smallFontSize),
			fontWeight: this.getConfigData('baseFontArial', 'fontWeight', 'bold'),
			wordWrap: true,
			fill: this.getConfigData('baseFontArial', 'fill', [standardColor]),
		});

		this._cheatFontArial = new PIXI.TextStyle({
			fontFamily: this.getConfigData('cheatFontArial', 'fontFamily', baseFontFamily),
			fontSize: this.getConfigData('cheatFontArial', 'fontSize', smallFontSize),
			fontStyle: this.getConfigData('cheatFontArial', 'fontStyle', 'italic'),
			fontWeight: this.getConfigData('cheatFontArial', 'fontWeight', 'bold'),
			fill: this.getConfigData('cheatFontArial', 'fill', ['#00ff99']),
		});

		this._featureTextStyle = new PIXI.TextStyle({
			align: this.getConfigData('featureTextStyle', 'align', 'center'),
			fontFamily: this.getConfigData('featureTextStyle', 'fontFamily', baseFontFamily),
			fontSize: this.getConfigData('featureTextStyle', 'fontSize', baseFontSize),
			fontWeight: this.getConfigData('featureTextStyle', 'fontWeight', 'bold'),
			fill: this.getConfigData('featureTextStyle', 'fill', ['#ffffff']),
			wordWrap: true,
		});

		this._featureScreenTextStyle = new PIXI.TextStyle({
			align: this.getConfigData('featureScreenTextStyle', 'align', 'left'),
			fontFamily: this.getConfigData('featureScreenTextStyle', 'fontFamily', baseFontFamily),
			fontSize: this.getConfigData('featureScreenTextStyle', 'fontSize', notTooSmallFontSize),
			fontWeight: this.getConfigData('featureScreenTextStyle', 'fontWeight', 'bold'),
			fill: this.getConfigData('featureScreenTextStyle', 'fill', ['#ffffff']),
			wordWrap: true,
			wordWrapWidth: this._resolutionManager.resolution.width * (580 / this._resolutionManager.resolution.width),
		});

		this._featureScreenCaptionTextStyle = new PIXI.TextStyle({
			align: this.getConfigData('featureScreenCaptionTextStyle', 'align', 'left'),
			fontFamily: this.getConfigData('featureScreenCaptionTextStyle', 'fontFamily', baseFontFamily),
			fontSize: this.getConfigData('featureScreenCaptionTextStyle', 'fontSize', notTooSmallFontSize),
			fontWeight: this.getConfigData('featureScreenCaptionTextStyle', 'fontWeight', 'bold'),
			fill: this.getConfigData('featureScreenCaptionTextStyle', 'fill', ['#ffffff']),
			wordWrap: true,
			wordWrapWidth: this._resolutionManager.resolution.width,
		});

		if (locale === 'ka') {
			this._featureTextStyle.padding = 10;
		}

		this._titlesFont = new PIXI.TextStyle({
			align: this.getConfigData('titlesFont', 'align', 'center'),
			fontFamily: this.getConfigData('titlesFont', 'fontFamily', specialFont),
			fontWeight: this.getConfigData('titlesFont', 'fontWeight', '900'),
			fill: this.getConfigData('titlesFont', 'fill', [standardColor]),
			fontSize: this.getConfigData('titlesFont', 'fontSize', bigFontSize),
			wordWrap: true,
			padding: this.getConfigData('titlesFont', 'padding', 10),
			breakWords: true,
			wordWrapWidth: this._resolutionManager.resolution.width * 0.8,
		});

		this._specialTitlesFont = new PIXI.TextStyle({
			align: this.getConfigData('specialTitlesFont', 'align', 'center'),
			fontFamily: this.getConfigData('specialTitlesFont', 'fontFamily', specialFont),
			fontWeight: this.getConfigData('specialTitlesFont', 'fontWeight', '900'),
			fill: this.getConfigData('specialTitlesFont', 'fill', [standardColor]),
			fontSize: this.getConfigData('specialTitlesFont', 'fontSize', bigFontSize),
			wordWrap: true,
			padding: this.getConfigData('specialTitlesFont', 'padding', 10),
		});

		this._subtitlesFont = new PIXI.TextStyle({
			align: this.getConfigData('subtitlesFont', 'align', 'center'),
			fontFamily: this.getConfigData('subtitlesFont', 'fontFamily', baseFontFamily),
			fontWeight: this.getConfigData('subtitlesFont', 'fontWeight', '600'),
			fontStyle: this.getConfigData('subtitlesFont', 'fontStyle', 'italic'),
			fill: this.getConfigData('subtitlesFont', 'fill', [standardColor]),
			fontSize: this.getConfigData('subtitlesFont', 'fontSize', middleFontSize),
		});

		this._breadTextFont = new PIXI.TextStyle({
			fontFamily: this.getConfigData('breadTextFont', 'fontFamily', baseFontFamily),
			fontSize: this.getConfigData('breadTextFont', 'fontSize', baseFontSize),
			align: this.getConfigData('breadTextFont', 'align', 'left'),
			fill: this.getConfigData('breadTextFont', 'fill', [standardColor]),
			wordWrap: true,
			wordWrapWidth: this._resolutionManager.resolution.width * 0.8,
		});

		this._gameRulesTextStyle = new PIXI.TextStyle({
			fontFamily: this.getConfigData('gameRulesTextStyle', 'fontFamily', baseFontFamily),
			fontSize: this.getConfigData('gameRulesTextStyle', 'fontSize', baseFontSize),
			align: this.getConfigData('gameRulesTextStyle', 'align', 'left'),
			fill: this.getConfigData('gameRulesTextStyle', 'fill', [standardColor]),
			wordWrap: true,
			wordWrapWidth: this._resolutionManager.resolution.width * 0.8,
		});

		this._breadTextHeavyFont = new PIXI.TextStyle({
			fontFamily: this.getConfigData('breadTextHeavyFont', 'fontFamily', baseFontFamily),
			fontSize: this.getConfigData('breadTextHeavyFont', 'fontSize', lowerMiddleFontSize),
			align: this.getConfigData('breadTextHeavyFont', 'align', 'left'),
			fill: this.getConfigData('breadTextHeavyFont', 'fill', [ocurrenceColor]),
			fontWeight: this.getConfigData('breadTextHeavyFont', 'fontWeight', '900'),
			wordWrap: true,
		});

		this._breadTextHeavyWhiteFont = new PIXI.TextStyle({
			fontFamily: this.getConfigData('breadTextHeavyWhiteFont', 'fontFamily', baseFontFamily),
			fontSize: this.getConfigData('breadTextHeavyWhiteFont', 'fontSize', lowerMiddleFontSize),
			align: this.getConfigData('breadTextHeavyWhiteFont', 'align', 'left'),
			fill: this.getConfigData('breadTextHeavyWhiteFont', 'fill', [standardColor]),
			fontWeight: this.getConfigData('breadTextHeavyWhiteFont', 'fontWeight', '900'),
			wordWrap: true,
		});

		this._guiInfoNumbers = new PIXI.TextStyle({
			fontFamily: this.getConfigData('guiInfoNumbers', 'fontFamily', strongFontFamily),
			fontWeight: this.getConfigData('guiInfoNumbers', 'fontWeight', '600'),
			fontSize: this.getConfigData('guiInfoNumbers', 'fontSize', this._device.isMobile ? baseFontSize + 2 : baseFontSize),
			fill: this.getConfigData('guiInfoNumbers', 'fill', [standardColor]),
			stroke: this.getConfigData('guiInfoNumbers', 'stroke', 'white'),
			strokeThickness: this.getConfigData('guiInfoNumbers', 'strokeThickness', 1),
		});

		this._guiInfoText = new PIXI.TextStyle({
			fontFamily: this.getConfigData('guiInfoText', 'fontFamily', baseFontFamily),
			fontSize: this.getConfigData('guiInfoText', 'fontSize', baseFontSize),
			fontWeight: this.getConfigData('guiInfoText', 'fontWeight', '600'),
			fill: this.getConfigData('guiInfoText', 'fill', [standardColor]),
			stroke: this.getConfigData('guiInfoText', 'stroke', 'white'),
			strokeThickness: this.getConfigData('guiInfoText', 'strokeThickness', 1),
		});

		this._guiInfoWinFieldNumbers = new PIXI.TextStyle({
			fontFamily: this.getConfigData('guiInfoWinFieldNumbers', 'fontFamily', strongFontFamily),
			fontWeight: this.getConfigData('guiInfoWinFieldNumbers', 'fontWeight', '600'),
			fontSize: this.getConfigData('guiInfoWinFieldNumbers', 'fontSize', this._device.isMobile ? baseFontSize + 2 : baseFontSize),
			fill: this.getConfigData('guiInfoWinFieldNumbers', 'fill', [standardColor]),
			stroke: this.getConfigData('guiInfoWinFieldNumbers', 'stroke', 'white'),
			strokeThickness: this.getConfigData('guiInfoWinFieldNumbers', 'strokeThickness', 1),
		});

		this._gameSpecificWinFieldNumbers = new PIXI.TextStyle({
			fontFamily: this.getConfigData('gameSpecificWinFieldNumbers', 'fontFamily', strongFontFamily),
			fontWeight: this.getConfigData('gameSpecificWinFieldNumbers', 'fontWeight', '600'),
			fontSize: this.getConfigData('gameSpecificWinFieldNumbers', 'fontSize', this._device.isMobile ? baseFontSize + 2 : baseFontSize),
			fill: this.getConfigData('gameSpecificWinFieldNumbers', 'fill', [standardColor]),
			stroke: this.getConfigData('gameSpecificWinFieldNumbers', 'stroke', 'white'),
			strokeThickness: this.getConfigData('gameSpecificWinFieldNumbers', 'strokeThickness', 1),
		});

		this._guiInfoWinFieldText = new PIXI.TextStyle({
			fontFamily: this.getConfigData('guiInfoWinFieldText', 'fontFamily', baseFontFamily),
			fontWeight: this.getConfigData('guiInfoWinFieldText', 'fontWeight', '600'),
			fontSize: this.getConfigData('guiInfoWinFieldText', 'fontSize', baseFontSize),
			fill: this.getConfigData('guiInfoWinFieldText', 'fill', [standardColor]),
			stroke: this.getConfigData('guiInfoWinFieldText', 'stroke', 'white'),
			strokeThickness: this.getConfigData('guiInfoWinFieldText', 'strokeThickness', 1),
		});

		const menuLeading: number = locale === 'th' ? 0 : 7;
		const menuSpacing: number = locale === 'th' ? 0 : 5;

		this._mainMenuTitle = new PIXI.TextStyle({
			align: this.getConfigData('mainMenuTitle', 'align', 'center'),
			fontFamily: this.getConfigData('mainMenuTitle', 'fontFamily', specialFont),
			fontSize: this.getConfigData('mainMenuTitle', 'fontSize', biggestFontSize),
			fill: this.getConfigData('mainMenuTitle', 'fill', [titleColor]),
			stroke: this.getConfigData('mainMenuTitle', 'stroke', '#bbbb00'),
			strokeThickness: this.getConfigData('mainMenuTitle', 'strokeThickness', 2),
			letterSpacing: this.getConfigData('mainMenuTitle', 'letterSpacing', menuSpacing),
			wordWrap: true,
			lineHeight: this.getConfigData('mainMenuTitle', 'lineHeight', biggestFontSize * 0.9),
			padding: this.getConfigData('mainMenuTitle', 'padding', 10),
			dropShadow: this.getConfigData('mainMenuTitle', 'dropShadow', false),
			dropShadowAngle: this.getConfigData('mainMenuTitle', 'dropShadowAngle', 1),
			dropShadowBlur: this.getConfigData('mainMenuTitle', 'dropShadowBlur', 1),
			dropShadowColor: this.getConfigData('mainMenuTitle', 'dropShadowColor', blackColor),
			dropShadowDistance: this.getConfigData('mainMenuTitle', 'dropShadowDistance', 1),
		});

		this._mainMenuBread = new PIXI.TextStyle({
			fontFamily: this.getConfigData('mainMenuBread', 'fontFamily', specialFont),
			fontSize: this.getConfigData('mainMenuBread', 'fontSize', bigFontSize),
			fill: this.getConfigData('mainMenuBread', 'fill', [standardColor]),
			stroke: this.getConfigData('mainMenuBread', 'stroke', '#bbbb00'),
			strokeThickness: this.getConfigData('mainMenuBread', 'strokeThickness', 2),
			letterSpacing: this.getConfigData('mainMenuBread', 'letterSpacing', menuSpacing),
			wordWrap: true,
		});

		const settingsLeading: number = locale === 'th' ? 0 : 7;
		const settingsSpacing: number = locale === 'th' ? 0 : 5;

		this._settingsSubtitle = new PIXI.TextStyle({
			fontFamily: this.getConfigData('settingsSubtitle', 'fontFamily', specialFont),
			align: this.getConfigData('settingsSubtitle', 'align', 'center'),
			fontSize: this.getConfigData('settingsSubtitle', 'fontSize', biggestFontSize),
			fill: this.getConfigData('settingsSubtitle', 'fill', ['#ffe846']),
			leading: this.getConfigData('settingsSubtitle', 'leading', settingsLeading),
			letterSpacing: this.getConfigData('settingsSubtitle', 'letterSpacing', settingsSpacing),
		});

		this._settingsDescription = new PIXI.TextStyle({
			fontFamily: this.getConfigData('settingsDescription', 'fontFamily', specialFont),
			align: this.getConfigData('settingsDescription', 'align', 'left'),
			fontSize: this.getConfigData('settingsDescription', 'fontSize', middleFontSize),
			fill: this.getConfigData('settingsDescription', 'fill', ['#ffe846']),
			leading: this.getConfigData('settingsDescription', 'leading', settingsLeading),
			letterSpacing: this.getConfigData('settingsDescription', 'letterSpacing', 0),
			lineHeight: this.getConfigData('settingsDescription', 'lineHeight', middleFontSize * 0.95),
			wordWrap: true,
		});

		this._autoplaySettingsDescription = new PIXI.TextStyle({
			fontFamily: this.getConfigData('autoplaySettingsDescription', 'fontFamily', specialFont),
			align: this.getConfigData('autoplaySettingsDescription', 'align', 'left'),
			fontSize: this.getConfigData('autoplaySettingsDescription', 'fontSize', middleFontSize),
			fill: this.getConfigData('autoplaySettingsDescription', 'fill', ['#ffe846']),
			leading: this.getConfigData('autoplaySettingsDescription', 'leading', settingsLeading),
			letterSpacing: this.getConfigData('autoplaySettingsDescription', 'letterSpacing', 0),
			lineHeight: this.getConfigData('autoplaySettingsDescription', 'lineHeight', middleFontSize * 0.95),
		});

		this._autoplaySettingsDescription.wordWrapWidth = this._resolutionManager.resolution.width * 0.6;
		if (locale === 'th' || locale === 'zh-cn' || locale === 'zh-tw') {
			this._autoplaySettingsDescription.fontSize = middleFontSize;
		} else if (locale === 'sl' || locale === 'vi') {
			this._autoplaySettingsDescription.fontSize = notTooSmallFontSize;
		} else {
			this._autoplaySettingsDescription.fontSize = baseFontSize;
		}

		this._buttonMenuTitleStyle = new PIXI.TextStyle({
			align: this.getConfigData('buttonMenuTitleStyle', 'align', 'center'),
			fontFamily: this.getConfigData('buttonMenuTitleStyle', 'fontFamily', specialFont),
			fontSize: this.getConfigData('buttonMenuTitleStyle', 'fontSize', biggestFontSize),
			fill: this.getConfigData('buttonMenuTitleStyle', 'fill', [titleColor]),
			stroke: this.getConfigData('buttonMenuTitleStyle', 'stroke', '#bbbb00'),
			strokeThickness: this.getConfigData('buttonMenuTitleStyle', 'strokeThickness', 2),
			letterSpacing: this.getConfigData('buttonMenuTitleStyle', 'letterSpacing', menuSpacing),
			wordWrap: true,
			lineHeight: this.getConfigData('buttonMenuTitleStyle', 'lineHeight', biggestFontSize * 0.9),
			padding: this.getConfigData('buttonMenuTitleStyle', 'padding', 10),
			dropShadow: this.getConfigData('buttonMenuTitleStyle', 'dropShadow', false),
			dropShadowAngle: this.getConfigData('buttonMenuTitleStyle', 'dropShadowAngle', 1),
			dropShadowBlur: this.getConfigData('buttonMenuTitleStyle', 'dropShadowBlur', 1),
			dropShadowColor: this.getConfigData('buttonMenuTitleStyle', 'dropShadowColor', blackColor),
			dropShadowDistance: this.getConfigData('buttonMenuTitleStyle', 'dropShadowDistance', 1),
		});

		this._buttonBaseStyle = new PIXI.TextStyle({
			fontFamily: this.getConfigData('buttonBaseStyle', 'fontFamily', strongFontFamily),
			fontSize: this.getConfigData('buttonBaseStyle', 'fontSize', middleFontSize),
			fill: this.getConfigData('buttonBaseStyle', 'fill', ['#ffff66']),
			dropShadow: this.getConfigData('buttonBaseStyle', 'dropShadow', true),
			dropShadowBlur: this.getConfigData('buttonBaseStyle', 'dropShadowBlur', 3),
			dropShadowDistance: this.getConfigData('buttonBaseStyle', 'dropShadowDistance', 2),
			dropShadowColor: this.getConfigData('buttonBaseStyle', 'dropShadowColor', blackColor),
		});

		this._smallButtonBaseStyle = new PIXI.TextStyle({
			fontFamily: this.getConfigData('smallButtonBaseStyle', 'fontFamily', strongFontFamily),
			fontSize: this.getConfigData('smallButtonBaseStyle', 'fontSize', smallFontSize + 4),
			fill: this.getConfigData('smallButtonBaseStyle', 'fill', ['#ffff66']),
			stroke: this.getConfigData('smallButtonBaseStyle', 'stroke', '#bbbb00'),
			strokeThickness: this.getConfigData('smallButtonBaseStyle', 'strokeThickness', 2),
		});

		this._buttonOverStyle = new PIXI.TextStyle({
			fontFamily: this.getConfigData('buttonOverStyle', 'fontFamily', strongFontFamily),
			fontSize: this.getConfigData('buttonOverStyle', 'fontSize', middleFontSize),
			fill: this.getConfigData('buttonOverStyle', 'fill', [standardColor]),
			dropShadow: this.getConfigData('buttonOverStyle', 'dropShadow', true),
			dropShadowBlur: this.getConfigData('buttonOverStyle', 'dropShadowBlur', 3),
			dropShadowDistance: this.getConfigData('buttonOverStyle', 'dropShadowDistance', 2),
			dropShadowColor: this.getConfigData('buttonOverStyle', 'dropShadowColor', blackColor),
		});

		this._buttonDownStyle = new PIXI.TextStyle({
			fontFamily: this.getConfigData('buttonDownStyle', 'fontFamily', strongFontFamily),
			fontSize: this.getConfigData('buttonDownStyle', 'fontSize', middleFontSize),
			fill: this.getConfigData('buttonDownStyle', 'fill', ['#dddddd']),
			dropShadow: this.getConfigData('buttonDownStyle', 'dropShadow', true),
			dropShadowBlur: this.getConfigData('buttonDownStyle', 'dropShadowBlur', 3),
			dropShadowDistance: this.getConfigData('buttonDownStyle', 'dropShadowDistance', 2),
			dropShadowColor: this.getConfigData('buttonDownStyle', 'dropShadowColor', blackColor),
		});

		this._buttonDisabledStyle = new PIXI.TextStyle({
			fontFamily: this.getConfigData('buttonDisabledStyle', 'fontFamily', strongFontFamily),
			fontSize: this.getConfigData('buttonDisabledStyle', 'fontSize', middleFontSize),
			fill: this.getConfigData('buttonDisabledStyle', 'fill', ['#666666']),
			dropShadow: this.getConfigData('buttonDisabledStyle', 'dropShadow', true),
			dropShadowBlur: this.getConfigData('buttonDisabledStyle', 'dropShadowBlur', 3),
			dropShadowDistance: this.getConfigData('buttonDisabledStyle', 'dropShadowDistance', 2),
			dropShadowColor: this.getConfigData('buttonDisabledStyle', 'dropShadowColor', blackColor),
		});

		this._limitErrorTextStyle = new PIXI.TextStyle({
			fontFamily: this.getConfigData('limitErrorTextStyle', 'fontFamily', strongFontFamily),
			fontSize: this.getConfigData('limitErrorTextStyle', 'fontFamily', notTooSmallFontSize),
			align: this.getConfigData('limitErrorTextStyle', 'fontFamily', 'left'),
			wordWrap: true,
			wordWrapWidth: this._resolutionManager.resolution.width * 0.6,
			fill: this.getConfigData('limitErrorTextStyle', 'fontFamily', [errorColor]),
		});

		this._infoPopup = new PIXI.TextStyle({
			fontFamily: this.getConfigData('infoPopup', 'fontFamily', baseFontFamily),
			fontSize: this.getConfigData('infoPopup', 'fontSize', baseFontSize),
			fontWeight: this.getConfigData('infoPopup', 'fontWeight', '700'),
			fill: this.getConfigData('infoPopup', 'fill', [standardColor]),
			stroke: this.getConfigData('infoPopup', 'stroke', '#4a1850'),
			strokeThickness: this.getConfigData('infoPopup', 'strokeThickness', 3),
			wordWrap: true,
			wordWrapWidth: 600 - 220,
			align: this.getConfigData('infoPopup', 'align', 'center'),
		});

		this._spinsLeftStyle = new PIXI.TextStyle({
			fontFamily: this.getConfigData('spinsLeftStyle', 'fontFamily', baseFontFamily),
			fontSize: this.getConfigData('spinsLeftStyle', 'fontSize', 20),
			fontWeight: this.getConfigData('spinsLeftStyle', 'fontWeight', 'bold'),
			fill: this.getConfigData('spinsLeftStyle', 'fill', [standardColor]),
			stroke: this.getConfigData('spinsLeftStyle', 'stroke', blackColor),
			strokeThickness: this.getConfigData('spinsLeftStyle', 'strokeThickness', 4),
		});

		this._historyBoxFont = new PIXI.TextStyle({
			fontFamily: this.getConfigData('historyBoxFont', 'fontFamily', specialFont),
			fontSize: this.getConfigData('historyBoxFont', 'fontFamily', 20),
			fontWeight: this.getConfigData('historyBoxFont', 'fontFamily', 'normal'),
			fill: this.getConfigData('historyBoxFont', 'fontFamily', [standardColor]),
		});

		this._historyBoxMultiplierFont = new PIXI.TextStyle({
			fontFamily: this.getConfigData('historyBoxMultiplierFont', 'fontFamily', specialFont),
			fontSize: this.getConfigData('historyBoxMultiplierFont', 'fontSize', baseFontSize),
			fontWeight: this.getConfigData('historyBoxMultiplierFont', 'fontWeight', 'normal'),
			fill: this.getConfigData('historyBoxMultiplierFont', 'fill', [standardColor]),
		});
	}

	protected getFontFamily(locale: string, fontMap: ILocaleFontMap): string {
		if (!fontMap.hasOwnProperty(locale)) {
			return fontMap.standard;
		}

		return fontMap[locale];
	}

	protected getConfigData(configName: string, parameterName: string, parameterByDefault: any): any {
		const config = this._config.getConfigByName(configName) as RecordData;

		if (!config) {
			return parameterByDefault;
		}

		const parameterDataFromConfig = config.getParameter(parameterName);

		if (parameterDataFromConfig instanceof Array) {
			return parameterDataFromConfig.length > 0 ? config.getParameter(parameterName) : parameterByDefault;
		}

		return parameterDataFromConfig ? parameterDataFromConfig : parameterByDefault;
	}

	public get baseFontArial(): PIXI.TextStyle {
		return this._baseFontArial;
	}

	public get cheatFontArial(): PIXI.TextStyle {
		return this._cheatFontArial;
	}

	public get guiInfoText(): PIXI.TextStyle {
		return this._guiInfoText;
	}

	public get guiInfoNumbers(): PIXI.TextStyle {
		return this._guiInfoNumbers;
	}

	public get guiInfoWinFieldText(): PIXI.TextStyle {
		return this._guiInfoWinFieldText;
	}

	public get guiInfoWinFieldNumbers(): PIXI.TextStyle {
		return this._guiInfoWinFieldNumbers;
	}

	public get titlesFont(): PIXI.TextStyle {
		return this._titlesFont;
	}

	public get specialTitlesFont(): PIXI.TextStyle {
		return this._specialTitlesFont;
	}

	public get subtitlesFont(): PIXI.TextStyle {
		return this._subtitlesFont;
	}

	public get breadTextFont(): PIXI.TextStyle {
		return this._breadTextFont;
	}

	public get breadTextHeavyFont(): PIXI.TextStyle {
		return this._breadTextHeavyFont;
	}

	public get breadTextHeavyWhiteFont(): PIXI.TextStyle {
		return this._breadTextHeavyWhiteFont;
	}

	public get mainMenuTitle(): PIXI.TextStyle {
		return this._mainMenuTitle;
	}

	public get mainMenuBread(): PIXI.TextStyle {
		return this._mainMenuBread;
	}

	public get settingsSubtitle(): PIXI.TextStyle {
		return this._settingsSubtitle;
	}

	public get settingsDescription(): PIXI.TextStyle {
		return this._settingsDescription;
	}

	public get autoplaySettingsDescription(): PIXI.TextStyle {
		return this._autoplaySettingsDescription;
	}

	public get buttonMenuTitleStyle(): PIXI.TextStyle {
		return this._buttonMenuTitleStyle;
	}

	public get buttonBaseStyle(): PIXI.TextStyle {
		return this._buttonBaseStyle;
	}

	public get smallButtonBaseStyle(): PIXI.TextStyle {
		return this._smallButtonBaseStyle;
	}

	public get buttonOverStyle(): PIXI.TextStyle {
		return this._buttonOverStyle;
	}

	public get buttonDownStyle(): PIXI.TextStyle {
		return this._buttonDownStyle;
	}

	public get buttonDisabledStyle(): PIXI.TextStyle {
		return this._buttonDisabledStyle;
	}

	public get infoPopup(): PIXI.TextStyle {
		return this._infoPopup;
	}

	public get spinsLeftStyle(): PIXI.TextStyle {
		return this._spinsLeftStyle;
	}

	public get featureTextStyle(): PIXI.TextStyle {
		return this._featureTextStyle;
	}

	public get featureScreenTextStyle(): PIXI.TextStyle {
		return this._featureScreenTextStyle;
	}

	public get featureScreenCaptionTextStyle(): PIXI.TextStyle {
		return this._featureScreenCaptionTextStyle;
	}

	public get freeRoundsCaptionStyle(): PIXI.TextStyle {
		return this._freeRoundsCaptionStyle;
	}

	public get freeRoundsTextStyle(): PIXI.TextStyle {
		return this._freeRoundsTextStyle;
	}

	public get freeRoundsDisclaimerStyle(): PIXI.TextStyle {
		return this._freeRoundsDisclaimerStyle;
	}

	public get freeRoundsSmallTextStyle(): PIXI.TextStyle {
		return this._freeRoundsSmallTextStyle;
	}

	public get freeRoundsButtonTextStyle(): PIXI.TextStyle {
		return this._freeRoundsButtonTextStyle;
	}

	public get freeRoundsSuperSmallTextStyle(): PIXI.TextStyle {
		return this._freeRoundsSuperSmallTextStyle;
	}

	public get limitErrorTextStyle(): PIXI.TextStyle {
		return this._limitErrorTextStyle;
	}

	public get gameRulesTextStyle(): PIXI.TextStyle {
		return this._gameRulesTextStyle;
	}

	public get historyBoxFont(): PIXI.TextStyle {
		return this._historyBoxFont;
	}

	public get historyBoxMultiplierFont(): PIXI.TextStyle {
		return this._historyBoxMultiplierFont;
	}

	public get gameSpecificWinFieldNumbers(): PIXI.TextStyle {
		return this._gameSpecificWinFieldNumbers;
	}
}



// WEBPACK FOOTER //
// ./@fantasmagames/core/src/display/texts/TextStyles.ts